import React, { useState } from 'react';
import { Row, Col, Collapse } from 'reactstrap';
import { LineChart, Line, YAxis, ResponsiveContainer, ReferenceLine, Tooltip } from 'recharts';
import { Heart, Activity, TrendingUp, Zap, ChevronDown, ChevronUp } from 'lucide-react';

const vitalData = {
  HeartRate: {
    icon: Heart,
    title: "Heart Rate",
    unit: "BPM",
    color: "#FF6B6B",
    bgColor: "#FFF5F5",
    domain: [40, 120]
  },
  SystolicBp: {
    icon: Activity,
    title: "Systolic BP",
    unit: "mmHg",
    color: "#4ECDC4",
    bgColor: "#F0FFFD",
    domain: [80, 180]
  },
  DiastolicBp: {
    icon: Activity,
    title: "Diastolic BP",
    unit: "mmHg",
    color: "#45B7D1",
    bgColor: "#F0FAFF",
    domain: [40, 120]
  },
  HeartRateVariability: {
    icon: TrendingUp,
    title: "HRV",
    unit: "ms",
    color: "#96CEB4",
    bgColor: "#F3FBF7",
    domain: [0, 100]
  },
  StressIndex: {
    icon: Zap,
    title: "Stress Index",
    unit: "",
    color: "#D4A5A5",
    bgColor: "#FFF7F7",
    domain: [0, 100]
  }
};

const MeasurementRow = ({ measurement, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => setIsOpen(!isOpen);

  const renderVitalSummary = (vitalKey) => {
    const vital = measurement[vitalKey];
    if (!vital || vital.values.length === 0) return null;

    const Icon = vitalData[vitalKey].icon;
    
    return (
      <div className="d-flex align-items-center mx-3" style={{ 
        backgroundColor: vitalData[vitalKey].bgColor,
        padding: '10px 16px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <div className="d-flex align-items-center" style={{ marginRight: '12px' }}>
          <Icon size={20} style={{ color: vitalData[vitalKey].color }} />
        </div>
        <div>
          <small className="text-muted">{vitalData[vitalKey].title}</small>
          <h6 className="mb-0" style={{ 
            color: vitalData[vitalKey].color,
            fontSize: '1.1rem',
            fontWeight: '600'
          }}>
            {vital.average?.toFixed(0)} <span style={{ fontSize: '0.8rem' }}>{vitalData[vitalKey].unit}</span>
          </h6>
        </div>
      </div>
    );
  };

  const CustomizedDot = (props) => {
    const { cx, cy } = props;
    return (
      <circle 
        cx={cx} 
        cy={cy} 
        r={3} 
        fill={props.stroke}
        fillOpacity={0.8}
      />
    );
  };

  const renderValueLabel = (value) => {
    return value.toFixed(0);
  };

  const renderVitalChart = (vitalKey) => {
    const vital = measurement[vitalKey];
    if (!vital || vital.values.length === 0) return null;

    const data = vital.values.map(v => ({
      value: v.value,
      time: new Date(v.measuredAt * 1000).toLocaleTimeString()
    }));

    // Calculate min, max, and mid values for reference lines
    const values = data.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const mid = (min + max) / 2;

    return (
      <Col md={6} lg={4} className="mb-3">
        <div style={{ 
          backgroundColor: vitalData[vitalKey].bgColor,
          padding: '15px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0" style={{ color: vitalData[vitalKey].color }}>
              {vitalData[vitalKey].title}
            </h6>
            <div className="d-flex flex-column align-items-end">
              <small className="text-muted">
                Max: {max.toFixed(0)} {vitalData[vitalKey].unit}
              </small>
              <small className="text-muted">
                Min: {min.toFixed(0)} {vitalData[vitalKey].unit}
              </small>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <YAxis 
                domain={vitalData[vitalKey].domain}
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                width={30}
              />
              <ReferenceLine 
                y={max} 
                strokeDasharray="3 3" 
                stroke={vitalData[vitalKey].color} 
                strokeOpacity={0.3}
              />
              <ReferenceLine 
                y={mid} 
                strokeDasharray="3 3" 
                stroke={vitalData[vitalKey].color} 
                strokeOpacity={0.3}
              />
              <ReferenceLine 
                y={min} 
                strokeDasharray="3 3" 
                stroke={vitalData[vitalKey].color} 
                strokeOpacity={0.3}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div style={{ 
                        backgroundColor: '#18202E',
                        padding: '8px 12px',
                        border: `1px solid ${vitalData[vitalKey].color}`,
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                      }}>
                        <p style={{ 
                          color: '#F0EEEE',
                          margin: '0',
                          fontSize: '0.9rem'
                        }}>
                          <span style={{ color: vitalData[vitalKey].color }}>
                            {payload[0].value.toFixed(0)}
                          </span>
                          <small style={{ marginLeft: '4px' }}>
                            {vitalData[vitalKey].unit}
                          </small>
                        </p>
                        <p style={{ 
                          color: '#9CA3AF',
                          margin: '4px 0 0 0',
                          fontSize: '0.8rem'
                        }}>
                          {payload[0].payload.time}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
                cursor={{ stroke: vitalData[vitalKey].color, strokeWidth: 1, strokeDasharray: '3 3' }}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={vitalData[vitalKey].color}
                strokeWidth={2}
                dot={<CustomizedDot />}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Col>
    );
  };

  // Only show measurements that have data
  const hasData = Object.keys(vitalData).some(key => 
    measurement[key]?.values?.length > 0
  );

  if (!hasData) return null;

  return (
    <div className="mb-4 mt-1">
      <div 
        className="d-flex align-items-center p-4 rounded cursor-pointer"
        onClick={toggle}
        style={{ 
          cursor: 'pointer',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.05)'
        }}
      >
        <div className="mr-4">
          <h6 className="mb-1" style={{ color: '#2D3748' }}>Measurement {index + 1}</h6>
          <small className="text-muted">
            {new Date(measurement.HeartRate?.startTime * 1000).toLocaleTimeString()} - 
            {new Date(measurement.HeartRate?.endTime * 1000).toLocaleTimeString()}
          </small>
        </div>
        <div className="d-flex flex-grow-1 flex-wrap" style={{ gap: '12px' }}>
          {Object.keys(vitalData).map(key => renderVitalSummary(key))}
        </div>
        {isOpen ? 
          <ChevronUp size={20} className="ml-3 text-muted" /> : 
          <ChevronDown size={20} className="ml-3 text-muted" />
        }
      </div>
      
      <Collapse isOpen={isOpen}>
        <div className="mt-4">
          <Row>
            {Object.keys(vitalData).map(key => renderVitalChart(key))}
          </Row>
        </div>
      </Collapse>
    </div>
  );
};

export default MeasurementRow;